import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {FormArray, FormBuilder, FormGroup} from '@angular/forms';
import {AuthPolicyEditHelperPipe} from './auth-policy-edit-helper.pipe';
import {NgxSmartModalService} from 'ngx-smart-modal';
// TODO(RZ-2562): Replace legacy VO types (AuthPolicyVO, PolicyConfigVO, PolicyUserVO) with SDK equivalents
import {AuthPolicyVO, PolicyConfigVO, PolicyUserVO} from '../../../../generate/TopLevelDomainVO';
import {UserService} from "../../../../services/user.service";
import {PolicyType} from "../../../../model/PolicyType";

@Component({
    selector: 'auth-policy-edit',
    templateUrl: './auth-policy-edit.component.html',
    styleUrls: ['./auth-policy-edit.component.scss'],
    standalone: false
})
export class AuthPolicyEditComponent implements OnInit {
  @Input()
  newDelegation: boolean;
  @Input()
  authPolicy: AuthPolicyVO;
  @Output()
  authPolicyVOOutput = new EventEmitter<AuthPolicyVO>();
  @Output()
  cancelOutPut = new EventEmitter<boolean>();
  editAuthPolicyForm: FormGroup;
  modifiedAuthPolicy: AuthPolicyVO;
  controlLimit: number;
  nonTechnicalLimit: number;
  technicalLimit: number;
  authPolicyLimit: number;
  errors = Array<string>();

  constructor(private formBuilder: FormBuilder,
              public ngxSmartModalService: NgxSmartModalService,
              private userService: UserService
    ) {
  }


  ngOnInit() {
    this.modifiedAuthPolicy = Object.assign({}, this.authPolicy);
    this.initForm();
  }


  cancel() {
    this.cancelOutPut.emit(true);
  }

  removeAuthorizer(email: string) {
    this.modifiedAuthPolicy.users = this.modifiedAuthPolicy.users.filter(user => user.email !== email);
    this.initForm();
  }

  private initAuthPolicyUsersArray(authPolicyEditHelperPipe: AuthPolicyEditHelperPipe) {
    const formArr = new FormArray([]);
    this.modifiedAuthPolicy.users.forEach((policyUserVO) => {
      const control = authPolicyEditHelperPipe.transform(policyUserVO.permissions, 'permission', PolicyType.CONTROL) || policyUserVO.all;
      const nonTechnical = authPolicyEditHelperPipe.transform(policyUserVO.permissions, 'permission', PolicyType.NON_TECHNICAL) || policyUserVO.all;
      const technical = authPolicyEditHelperPipe.transform(policyUserVO.permissions, 'permission', PolicyType.TECHNICAL) || policyUserVO.all;
      const authPolicy = authPolicyEditHelperPipe.transform(policyUserVO.permissions, 'permission', PolicyType.AUTH_POLICY) || policyUserVO.all;
      const all = (control && nonTechnical && technical && authPolicy) || policyUserVO.all;
      let authPolicyUsers = this.formBuilder.group({
        firstName: policyUserVO.firstName,
        lastName: policyUserVO.lastName,
        name: policyUserVO.firstName + ' ' + policyUserVO.lastName,
        id: policyUserVO.id,
        email: policyUserVO.email,
        all: all,
        control: [{value: control, disabled: all}],
        nonTechnical: [{value: nonTechnical, disabled: all}],
        technical: [{value: technical, disabled: all}],
        authPolicy: [{value: authPolicy, disabled: all}],
      });
      formArr.push(authPolicyUsers);
    });

    return formArr;
  }


  private initPolicyConfig(authPolicyEditHelperPipe: AuthPolicyEditHelperPipe) {
    const control = authPolicyEditHelperPipe.transform(this.modifiedAuthPolicy, 'policy', PolicyType.CONTROL).policyThreshold;
    const nonTechnical = authPolicyEditHelperPipe.transform(this.modifiedAuthPolicy, 'policy', PolicyType.NON_TECHNICAL).policyThreshold;
    const technical = authPolicyEditHelperPipe.transform(this.modifiedAuthPolicy, 'policy', PolicyType.TECHNICAL).policyThreshold;
    const authPolicy = authPolicyEditHelperPipe.transform(this.modifiedAuthPolicy, 'policy', PolicyType.AUTH_POLICY).policyThreshold;
    this.controlLimit = control.maxLimit;
    this.nonTechnicalLimit = nonTechnical.maxLimit;
    this.technicalLimit = technical.maxLimit;
    this.authPolicyLimit = authPolicy.maxLimit;
    return this.formBuilder.group({
      control: control.thresholdType,
      controlValue: control.limit,
      nonTechnical: nonTechnical.thresholdType,
      nonTechnicalValue: nonTechnical.limit,
      technical: technical.thresholdType,
      technicalValue: technical.limit,
      authPolicy: authPolicy.thresholdType,
      authPolicyValue: authPolicy.limit,
    });
  }

  get formUsers(): FormArray {
    return this.editAuthPolicyForm.controls['users'] as FormArray;
  }

  onSubmit() {
    this.validateAuthPolicy();
    if (this.errors.length === 0) {
      this.authPolicyVOOutput.emit(this.modifiedAuthPolicy);
    }
  }

  validateAuthPolicy() {
    this.errors = new Array<string>();

    if (this.modifiedAuthPolicy.users.length < 1) {
      this.errors.push('Minimum One authorizer is required.');
    }
    let authPolicyEditHelperPipe = new AuthPolicyEditHelperPipe();
    const control = authPolicyEditHelperPipe.transform(this.modifiedAuthPolicy, 'policy', PolicyType.CONTROL,);
    const nonTechnical = authPolicyEditHelperPipe.transform(this.modifiedAuthPolicy, 'policy', PolicyType.NON_TECHNICAL);
    const technical = authPolicyEditHelperPipe.transform(this.modifiedAuthPolicy, 'policy', PolicyType.TECHNICAL);
    const authPolicy = authPolicyEditHelperPipe.transform(this.modifiedAuthPolicy, 'policy', PolicyType.AUTH_POLICY,);
    this.addThresholdErrors(control);
    this.addThresholdErrors(nonTechnical);
    this.addThresholdErrors(technical);
    this.addThresholdErrors(authPolicy);
  }

  addThresholdErrors(policyConfigVO: PolicyConfigVO) {
    if (policyConfigVO.policyThreshold.maxLimit === 0 || (policyConfigVO.policyThreshold.thresholdType === 'Limit' && policyConfigVO.policyThreshold.limit === 0)) {
      this.errors.push('Minimum thresholdType for ' + policyConfigVO.policyType + ' Change Request is 1');
    }
  }

  private onChanges() {
    this.editAuthPolicyForm.valueChanges.subscribe(authpolicy => {
      this.modifiedAuthPolicy = {users: [], policies: []};
      authpolicy.users.forEach(user => {
        let policyUserVO: PolicyUserVO = {};
        policyUserVO.id = user.id;
        policyUserVO.email = user.email;
        policyUserVO.lastName = user.lastName;
        policyUserVO.firstName = user.firstName;
        policyUserVO.all = user.all;
        policyUserVO.permissions = [
          {
            policyType: PolicyType.CONTROL,
            permission: user.control || user.all ? 'Approve' : 'None'
          },
          {
            policyType: PolicyType.NON_TECHNICAL,
            permission: user.nonTechnical || user.all ? 'Approve' : 'None'
          },
          {
            policyType: PolicyType.TECHNICAL,
            permission: user.technical || user.all ? 'Approve' : 'None'
          },
          {
            policyType: PolicyType.AUTH_POLICY,
            permission: user.authPolicy || user.all ? 'Approve' : 'None'
          }
        ];
        this.modifiedAuthPolicy.users.push(policyUserVO);
      });

      this.modifiedAuthPolicy.policies.push({
        policyType: PolicyType.CONTROL,
        policyThreshold: {
          thresholdType: authpolicy.policies.control,
          limit: this.editAuthPolicyForm.getRawValue().policies.controlValue

        }
      }, {
        policyType: PolicyType.NON_TECHNICAL,
        policyThreshold: {
          thresholdType: authpolicy.policies.nonTechnical,
          limit: this.editAuthPolicyForm.getRawValue().policies.nonTechnicalValue

        }
      }, {
        policyType: PolicyType.TECHNICAL,
        policyThreshold: {
          thresholdType: authpolicy.policies.technical,
          limit: this.editAuthPolicyForm.getRawValue().policies.technicalValue
        }
      }, {
        policyType: PolicyType.AUTH_POLICY,
        policyThreshold: {
          thresholdType: authpolicy.policies.authPolicy,
          limit: this.editAuthPolicyForm.getRawValue().policies.authPolicyValue

        }
      });

      setTimeout(() => this.initForm());
    });
  }

  private initForm() {
    const authPolicyEditHelperPipe = new AuthPolicyEditHelperPipe();
    this.editAuthPolicyForm = this.formBuilder.group({
      users: this.initAuthPolicyUsersArray(authPolicyEditHelperPipe),
      policies: this.initPolicyConfig(authPolicyEditHelperPipe)
    });

    this.onChanges();
  }

  addAuthorizer() {
    this.ngxSmartModalService.getModal('myModal').open();
  }

  addUserSubmit(user: PolicyUserVO) {
    if (user) {
      user.permissions = [{
        policyType: PolicyType.NON_TECHNICAL,
        permission: 'Approve'
      }, {
        policyType: PolicyType.TECHNICAL,
        permission: 'Approve'
      }, {
        policyType: PolicyType.AUTH_POLICY,
        permission: 'Approve'
      }, {
        policyType: PolicyType.CONTROL,
        permission: 'None'
      }];
      this.modifiedAuthPolicy.users.push(user);
    }
    this.initForm();
    this.ngxSmartModalService.getModal('myModal').close();

  }
}
